import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CssBaseline, CircularProgress, Box } from '@mui/material';

import { ThemeFromSettingsProvider } from './shared/theme/ThemeFromSettingsProvider';
import { AuthProvider, useAuth } from './shared/contexts/AuthContext';
import { SyncProvider, useSync } from './shared/contexts/SyncContext';
import { NavDrawerProvider } from './shared/contexts/NavDrawerContext';
import { TourProvider } from './tour/TourProvider';
import { NavDrawer } from './components/Layout/NavDrawer';
import { AppLayout } from './components/Layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { CataloguePage } from './pages/CataloguePage';
import { SettingsPage } from './pages/SettingsPage';
import { LookbookPage } from './pages/LookbookPage';
import { PortfoliosPage } from './pages/PortfoliosPage';

/* Protected route: redirect to /login if not authenticated, and force a
   password change first when the account is flagged (invite / admin reset). */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, mustChangePassword } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
};

/** Sync once every time the app loads (new tab or refresh), if authenticated and online. */
const AutoSync: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { triggerSync, isOnline } = useSync();
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !isOnline || hasSynced.current) return;
    hasSynced.current = true;
    triggerSync();
  }, [isAuthenticated, isOnline, triggerSync]);

  return null;
};

const AppRoutes: React.FC = () => (
  <>
    <AutoSync />
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* First-login: redeem an invite token to set a password (public). */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/invite" element={<AcceptInvitePage />} />
      {/* Forced / voluntary password change (authenticated, standalone). */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<CataloguePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="portfolios" element={<PortfoliosPage />} />
      </Route>
      {/* Standalone routes (outside AppLayout) — include NavDrawer separately */}
      <Route
        path="/lookbook"
        element={
          <ProtectedRoute>
            <>
              <NavDrawer />
              <LookbookPage />
            </>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </>
);

const BASENAME = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '') || '/';

export default function App() {
  return (
    <ThemeFromSettingsProvider>
      <CssBaseline />
      <BrowserRouter basename={BASENAME}>
        <AuthProvider>
          <SyncProvider>
            <NavDrawerProvider>
              <TourProvider>
                <AppRoutes />
              </TourProvider>
            </NavDrawerProvider>
          </SyncProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeFromSettingsProvider>
  );
}
